/**
 * Added the "rememberMe" parameter to the onSubmit callback.
 */

import { DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC } from '@/data/constants/dataKey';
import { NOT_APPLICABLE } from '@/data/constants/gtm';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { setDefaultRequest, useDefaultStore } from '@/data/Content/DefaultStore';
import { useGuest } from '@/data/Content/Guest';
import { loginFetcher, UserLogon } from '@/data/Content/Login';
import { logoutFetcher, useLogout } from '@/data/Content/Logout';
import { useNotifications } from '@/data/Content/Notifications';
import { SessionErrorContext } from '@/data/context/sessionError';
import { WC_TOKEN, WC_TRUSTED_TOKEN } from '@/data/forReactAndMobile/constants';
import {
	clearCurrentUserFromStorage,
	CurrentUser,
	saveCurrentUserToStorage,
} from '@/data/forReactAndMobile/SaveCurrentUser';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useRememberMeState } from '@/data/state/useRememberMeState';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { ErrorType } from '@/data/types/Error';
import { useUser } from '@/data/User';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { personMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personMutatorKeyMatcher';
import { isErrorType, processError } from '@/data/utils/processError';
import { useRedirectIfProtected } from '@/data/utils/useRedirectIfProtected';
import { GA4_AUTH_EVENTS } from 'presentation/events/constants/eventConstants';
import { useTrackAuthEvent } from 'presentation/events/hooks/useTrackAuthEvent';
import { GA4LoginCompleteEventPayload } from 'presentation/events/types/ga4Types';
import { SyntheticEvent, useCallback, useContext, useMemo } from 'react';
import { MIN_EXP_TIME } from 'shared/custom/constants/commonConstants';
import { mutate } from 'swr';

type ArgTypes = string | number;

export const useSessionError = () => {
	const { sessionError, setSessionError } = useContext(SessionErrorContext);
	const { user } = useUser();
	const { settings } = useSettings();
	const router = useNextRouter();
	const { langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { physicalStoreId } = useDefaultStore();
	const { handleLogout } = useLogout();
	const { notifyError } = useNotifications();
	const {
		actions: { setRememberMe },
	} = useRememberMeState();
	const { redirectIfProtected } = useRedirectIfProtected();

	const errorsNLS = useLocalization('SessionError');
	type errorNLSKeys = keyof typeof errorsNLS;
	const { guestLogout } = useGuest();

	const trackAuthEvent = useTrackAuthEvent();

	const message = useMemo(() => {
		if (!sessionError) {
			return null;
		} else {
			const messageKey = sessionError.messageKey as errorNLSKeys;
			const titleKey = sessionError.titleKey as errorNLSKeys;
			const errorParameters: [ArgTypes, ArgTypes, ArgTypes, ...ArgTypes[]] = ['', '', ''];
			const messageParameters = sessionError.errorParameters;
			if (messageParameters && messageParameters.length > 0) {
				errorParameters.splice(0, messageParameters.length, ...messageParameters);
			}
			const text =
				sessionError.messageKey in errorsNLS
					? errorsNLS[messageKey].t(errorParameters)
					: sessionError.errorMessage ?? '';
			const title = titleKey && titleKey in errorsNLS ? errorsNLS[titleKey].t() : '';

			return { text, title };
		}
	}, [errorsNLS, sessionError]);

	const onSubmit = useCallback(
		// eslint-disable-next-line complexity
		async ({ email = '', logonId = '', logonPassword, rememberMe }: UserLogon) => {
			const data = {
				logonId: (logonId || email).toLowerCase(),
				logonPassword,
			};
			// If an error is triggered we need to clean the storage session data to create a new one
			try {
				await logoutFetcher(true)(settings?.storeId ?? '', { updateCookies: 'true' }, params);
				clearCurrentUserFromStorage(process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '');
			} catch (e) {
				await guestLogout(); // call guestLogout to clear invalid cookies
				clearCurrentUserFromStorage(process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '');
			}

			const loginCompleteParams: GA4LoginCompleteEventPayload = {
				event: GA4_AUTH_EVENTS.LOGIN_COMPLETE,
				element_name: 'session error form',
				element_type: 'dialog',
				page: window.location.href,
				element_location: 'login dialog',
				status: 'success',
				error_type: NOT_APPLICABLE,
			};

			try {
				const resp = await loginFetcher(true)(
					settings?.storeId ?? '',
					{
						rememberMe,
						updateCookies: 'true',
					},
					data,
					params
				);

				await setDefaultRequest(
					{ defaultStore: physicalStoreId, fromPageName: '2' },
					settings?.storeId ?? '',
					langId,
					MIN_EXP_TIME,
					{
						headers: {
							[WC_TOKEN]: resp.WCToken,
							[WC_TRUSTED_TOKEN]: resp.WCTrustedToken,
						},
					}
				);

				setRememberMe(rememberMe);
				setSessionError(null);
				mutate(personMutatorKeyMatcher(EMPTY_STRING)); // current page
				mutate(personMutatorKeyMatcher(DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC), undefined);

				trackAuthEvent(loginCompleteParams);
				const currentUser = {
					...resp,
					userLoggedIn: true,
					isGuest: false,
					generatedCookie: true,
				} as CurrentUser;

				saveCurrentUserToStorage(
					process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '',
					currentUser
				);
			} catch (e) {
				const error: TransactionErrorResponse | ErrorType = processError(
					e as TransactionErrorResponse
				);
				notifyError(error);

				let errorCode = 'UNEXPECTED_ERROR';
				if (isErrorType(error)) {
					errorCode = error.error.errorKey || errorCode;
				}

				trackAuthEvent({
					...loginCompleteParams,
					status: 'error',
					error_type: errorCode,
				});
			}
		},
		[
			settings?.storeId,
			params,
			guestLogout,
			setRememberMe,
			setSessionError,
			trackAuthEvent,
			notifyError,
			physicalStoreId,
			langId,
		]
	);

	const handleCancel = useCallback(
		async (event?: SyntheticEvent) => {
			setSessionError(null);
			// Partial-authentication (remember-me) cancel needs a different path than a
			// normal session-error logout. The WC_PERSISTENT cookie re-establishes the
			// partially authenticated identity on the next request, so a plain logout +
			// SWR revalidation would just re-trigger PartialAuthError and re-open this
			// dialog in a loop. Instead purge the persistent identity + remember-me state
			// and reload so the page comes back as a clean generic guest.
			if (sessionError?.messageKey === 'PartialAuthError') {
				setRememberMe(false);
				clearCurrentUserFromStorage(process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '');
				try {
					await fetch('/api/reset-session?purgePersistent=true');
				} catch {
					// ignore: the reload below still drops in-memory session state
				}
				router.reload();
				return;
			}
			redirectIfProtected();
			await handleLogout(event);
		},
		[handleLogout, setSessionError, redirectIfProtected, sessionError, setRememberMe, router]
	);

	return {
		message,
		handleCancel,
		onSubmit,
		sessionError,
		setSessionError,
		logonId: user?.logonId ?? '',
	};
};
